/**
 * Admin Panel
 *
 * Manage tester access to the lab
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getAllTesters,
  addTester,
  updateTesterStatus,
  removeTester,
  Tester,
} from '../lib/testers';

export function AdminPanel() {
  const { user, isAdmin } = useAuth();
  const [testers, setTesters] = useState<Tester[]>([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadTesters();
  }, []);

  const loadTesters = async () => {
    try {
      setLoading(true);
      const allTesters = await getAllTesters();
      // Sort by addedAt descending (newest first)
      allTesters.sort((a, b) => b.addedAt.getTime() - a.addedAt.getTime());
      setTesters(allTesters);
    } catch (err) {
      console.error('Error loading testers:', err);
      setError('Failed to load testers');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTester = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !user.email || !newEmail.trim()) return;

    try {
      setAdding(true);
      setError(null);
      await addTester(newEmail.trim().toLowerCase(), user.email, newDisplayName.trim() || undefined);
      setSuccess(`Added ${newEmail} successfully!`);
      setNewEmail('');
      setNewDisplayName('');
      await loadTesters();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error adding tester:', err);
      setError('Failed to add tester. Make sure the email is valid.');
    } finally {
      setAdding(false);
    }
  };

  const handleToggleStatus = async (email: string, currentStatus: boolean) => {
    try {
      await updateTesterStatus(email, !currentStatus);
      await loadTesters();
    } catch (err) {
      console.error('Error toggling status:', err);
      setError('Failed to update status');
    }
  };

  const handleRemove = async (email: string) => {
    if (!confirm(`Remove ${email} from tester list?`)) return;

    try {
      await removeTester(email);
      await loadTesters();
    } catch (err) {
      console.error('Error removing tester:', err);
      setError('Failed to remove tester');
    }
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 text-lg font-medium">Admin access required</div>
        <p className="text-gray-600 mt-2">You do not have permission to access this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading testers...</p>
      </div>
    );
  }

  const activeCount = testers.filter((t) => t.active).length;
  const inactiveCount = testers.length - activeCount;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Tester Management</h2>
        <p className="text-gray-600">Manage who has access to the Heirloom Shopping Lab</p>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mt-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-sm text-blue-600 font-medium">Total Testers</div>
            <div className="text-2xl font-bold text-blue-900">{testers.length}</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-sm text-green-600 font-medium">Active</div>
            <div className="text-2xl font-bold text-green-900">{activeCount}</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 font-medium">Inactive</div>
            <div className="text-2xl font-bold text-gray-900">{inactiveCount}</div>
          </div>
        </div>
      </div>

      {/* Add New Tester */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Tester</h3>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
            <p className="text-sm text-green-600">{success}</p>
          </div>
        )}

        <form onSubmit={handleAddTester} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address *
              </label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="tester@example.com"
                required
                disabled={adding}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Display Name (optional)
              </label>
              <input
                type="text"
                value={newDisplayName}
                onChange={(e) => setNewDisplayName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="John Doe"
                disabled={adding}
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={adding || !newEmail.trim()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {adding ? 'Adding...' : 'Add Tester'}
          </button>
        </form>
      </div>

      {/* Testers List */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Tester List</h3>

        {testers.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No testers added yet</p>
        ) : (
          <div className="space-y-3">
            {testers.map((tester) => (
              <div
                key={tester.email}
                className={`border rounded-lg p-4 ${
                  tester.active ? 'bg-white' : 'bg-gray-50 opacity-75'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-gray-900">
                        {tester.displayName || tester.email}
                      </h4>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          tester.active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {tester.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {tester.displayName && (
                      <p className="text-sm text-gray-600 mt-1">{tester.email}</p>
                    )}
                    <div className="text-xs text-gray-500 mt-2">
                      Added by {tester.addedBy} on{' '}
                      {tester.addedAt.toLocaleDateString()}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleStatus(tester.email, tester.active)}
                      className={`px-3 py-1 text-sm font-medium rounded-md ${
                        tester.active
                          ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {tester.active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => handleRemove(tester.email)}
                      className="px-3 py-1 text-sm font-medium rounded-md bg-red-100 text-red-700 hover:bg-red-200"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
